import DateOfBirthField from '../components/DateOfBirthField';
import TermsAcceptance from '../components/TermsAcceptance';
import { isAdult } from '../utils/agePolicy';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  UserCircleIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  CalendarIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
  CheckIcon,
  XMarkIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { fetchWithAuth } from '../services/api';
import ProfileDeleteModal from '../components/ProfileDeleteModal';
import { countries, getCountryName } from '../utils/countries';
import { getCdnUrl } from '../utils/cdnConfig';
import './UserProfile.css';

const UserProfile = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [userQrCodes, setUserQrCodes] = useState([]);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isLoadingQrCodes, setIsLoadingQrCodes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // State for delete modal
  const [deletingProfile, setDeletingProfile] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // State for change password modal
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const getLocalizedCountryName = (countryCode) => {
    if (!countryCode) return '-';
    if (countryCode.toUpperCase() === 'MK') return t('userProfile.macedonia');

    const localeMap = { al: 'sq' };
    const locale = localeMap[lang] || lang;

    try {
      return new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode);
    } catch {
      return getCountryName(countryCode);
    }
  };

  // Check if user has reached their profile quota
  const isQuotaExceeded = user && profiles.length >= (user.maxProfiles || 1);

  const fetchUserData = useCallback(async () => {
    setIsLoadingPage(true);
    setError(null);
    setUpdateError(null);
    try {
      // Get user data
      const userData = await fetchWithAuth('/users/me');
      setUser(userData);
      setEditFormData({
        dateOfBirth: userData.dateOfBirth || '',
        ageConfirmed: false,
        fullName: userData.fullName || '',
        phone: userData.phone || '',
        address: userData.address || '',
        city: userData.city || '',
        country: userData.country || '',
        defaultLanguage: userData.defaultLanguage || 'ENGLISH',
      });

      if (userData.ageConfirmationRequired) {
        setIsEditing(true);
        setProfiles([]);
        setUserQrCodes([]);
        return;
      }
      // Get full profile data with images
      const profilesData = await fetchWithAuth('/users/me/profiles');
      setProfiles(profilesData || []);

    } catch (err) {
      console.error("Error fetching user data:", err);
      const authenticationFailed =
        !localStorage.getItem('jwtToken') ||
        /unauthorized|forbidden|authentication|jwt|token|expired/i.test(err.message || '');

      if (authenticationFailed) {
        localStorage.setItem('intendedDestination', location.pathname);
        navigate(`/${lang}/login`, { replace: true });
        return;
      }

      setError(err.message);
    } finally {
      setIsLoadingPage(false);
    }
  }, [navigate, lang, location.pathname]);

  const fetchUserQrCodes = useCallback(async () => {
    setIsLoadingQrCodes(true);
    try {
      const qrCodesData = await fetchWithAuth('/qrcodes/my-qrcodes');
      setUserQrCodes(qrCodesData || []);
    } catch (err) {
      console.error("Error fetching QR codes:", err);
      // Don't set error state for QR codes, just log it
    } finally {
      setIsLoadingQrCodes(false);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (user && !user.ageConfirmationRequired) fetchUserQrCodes();
  }, [user, fetchUserQrCodes]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setUpdateError(null);
    setUpdateSuccess(false);
    if (!isEditing && user) {
        setEditFormData({
            dateOfBirth: user.dateOfBirth || '',
        ageConfirmed: false,
        fullName: user.fullName || '',
            phone: user.phone || '',
            address: user.address || '',
            city: user.city || '',
            country: user.country || '',
            defaultLanguage: user.defaultLanguage || 'ENGLISH',
        });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setUpdateError(null);
    setUpdateSuccess(false);
    if (!isAdult(editFormData.dateOfBirth)) {
      setUpdateError(t('agePolicy.invalid'));
      return;
    }
    if (!editFormData.ageConfirmed) {
      setUpdateError(t('agePolicy.confirmRequired'));
      return;
    }
    setIsSaving(true);

    // Store the original language to detect changes
    const originalLanguage = user?.defaultLanguage;
    const newLanguage = editFormData.defaultLanguage;

    try {
      const updatedUser = await fetchWithAuth('/users/me/profile', {
        method: 'PUT',
        body: editFormData,
      });

      setUpdateSuccess(true);
      setIsEditing(false);
      await fetchUserData();

      // Trigger profileUpdated event to notify other components (like Navbar)
      window.dispatchEvent(new Event('profileUpdated'));

      // Check if language was changed and apply it immediately
      if (originalLanguage !== newLanguage) {
        const langCodeMap = {
          'MACEDONIAN': 'mk',
          'ALBANIAN': 'al',
          'TURKISH': 'tr',
          'ENGLISH': 'en',
          'GERMAN': 'de',
          'SERBIAN': 'sr',
          'CROATIAN': 'hr'
        };
        
        const newLangCode = langCodeMap[newLanguage] || 'en';
        
        // Apply language change immediately
        i18n.changeLanguage(newLangCode);
        
        // Update URL to reflect new language
        const currentPath = location.pathname;
        const pathWithoutLang = currentPath.replace(/^\/(mk|al|en|tr|hr|sr|de)/, '');
        navigate(`/${newLangCode}${pathWithoutLang || '/'}`);
        
        // Store language preference in localStorage for session persistence
        localStorage.setItem('selectedLanguage', newLangCode);
      }

    } catch (err) {
      console.error('Error updating profile:', err);
      setUpdateError(err.message || t('userProfile.error.updateFailed', 'Failed to update profile.'));
    } finally {
      setIsSaving(false);
    }
  };

  const DetailItem = ({ icon: Icon, label, value, valueTestId }) => (
    <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:text-left space-y-2 sm:space-y-0 sm:space-x-3 py-4 sm:py-2 border-b border-gray-100 last:border-b-0">
      <Icon className="h-6 w-6 sm:h-5 sm:w-5 text-gray-500 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
        <p className="text-base sm:text-base text-gray-900 font-medium break-words" data-testid={valueTestId}>{value || '-'}</p>
      </div>
    </div>
  );

  const FormInput = ({ label, name, value, onChange, type = 'text', readOnly = false, ...props }) => (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        className={`mt-1 block w-full px-3 py-2 border ${readOnly ? 'bg-gray-100' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}
        {...props}
      />
    </div>
  );

  // Component for Create Profile Button with quota handling
  const CreateProfileButton = ({ className = "", showIcon = true }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    
    if (isQuotaExceeded) {
      return (
        <div className="relative inline-block">
          <button
            disabled
            className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed ${className}`}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            data-testid="create-profile-button-disabled"
          >
            {showIcon && <PencilSquareIcon className="-ml-1 mr-2 h-5 w-5" />}
            {t('userProfile.createProfile', 'Create New Profile')}
          </button>
          {showTooltip && (
            <div className="absolute z-10 w-80 p-3 mt-2 text-sm bg-gray-900 text-white rounded-lg shadow-lg">
              <div className="mb-2 font-medium">{t('userProfile.quotaExceeded.title')}</div>
              <div className="space-y-1 text-xs">
                <div>• <Link to={`/${lang}/products`} className="text-blue-300 hover:text-blue-200 underline">{t('userProfile.quotaExceeded.buyProduct')}</Link> {t('userProfile.quotaExceeded.toIncreaseLimit')}</div>
                <div>• {t('userProfile.quotaExceeded.deleteProfile')}</div>
              </div>
              <div className="absolute top-0 left-6 transform -translate-y-1">
                <div className="w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <Link 
        to={`/${lang}/create-profile`} 
        className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-lg hover:shadow-xl transition-all duration-200 ${className}`}
        data-testid="create-profile-button-enabled"
      >
        {showIcon && <PencilSquareIcon className="-ml-1 mr-2 h-5 w-5" />}
        {t('userProfile.createProfile', 'Create New Profile')}
      </Link>
    );
  };

  const handleEditProfile = (profile) => {
    const currentLang = lang;
    navigate(`/${currentLang}/edit-profile/${profile.stringId || profile.id}`);
  };

  const handleDeleteProfile = (profile) => {
    setDeletingProfile(profile);
    setIsDeleteModalOpen(true);
  };

  const handleProfileDeleted = (profileId) => {
    // Remove the profile from the list
    setProfiles(prevProfiles => 
      prevProfiles.filter(profile => profile.id !== profileId)
    );
    // Refresh user data
    fetchUserData();
    // Dispatch event to notify navbar to update
    window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { profileId, action: 'deleted' } }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validation
    if (!passwordFormData.currentPassword || !passwordFormData.newPassword || !passwordFormData.confirmPassword) {
      setPasswordError(t('changePassword.allFieldsRequired'));
      return;
    }

    if (passwordFormData.newPassword.length < 8) {
      setPasswordError(t('changePassword.passwordTooShort'));
      return;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setPasswordError(t('changePassword.passwordMismatch'));
      return;
    }

    if (passwordFormData.currentPassword === passwordFormData.newPassword) {
      setPasswordError(t('changePassword.samePassword'));
      return;
    }

    setIsChangingPassword(true);

    try {
      await fetchWithAuth('/users/me/change-password', {
        method: 'POST',
        body: {
          currentPassword: passwordFormData.currentPassword,
          newPassword: passwordFormData.newPassword,
          confirmPassword: passwordFormData.confirmPassword
        }
      });

      setPasswordSuccess(true);
      setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowChangePasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);

    } catch (err) {
      console.error('Password change error:', err);
      if (err.message.includes('Current password is incorrect')) {
        setPasswordError(t('changePassword.incorrectPassword'));
      } else {
        setPasswordError(err.message || t('changePassword.networkError'));
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoadingPage && !user) {
    return (
      <div className="app">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-red-600 text-center">
            <p className="text-xl font-semibold">{t('userProfile.errorTitle')}</p>
            <p className="mt-2">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="max-w-4xl mx-auto px-2 sm:px-6 lg:px-8 py-4 sm:py-12">
        {user?.ageConfirmationRequired && <p role="alert" className="mb-4 rounded-md bg-amber-50 p-4 text-amber-900">{t('agePolicy.existingAccount')}</p>}
        {updateError && (
            <div className="mb-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow-md" role="alert">
                <p className="font-bold">{t('userProfile.updateErrorTitle', 'Update Failed')}</p>
                <p>{updateError}</p>
            </div>
        )}
        {user && <p className="mb-4 text-sm text-gray-700" data-testid="account-date-of-birth-display">{t('agePolicy.dateOfBirth')}: {user.dateOfBirth || '-'}</p>}
        {updateSuccess && (
            <div className="mb-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md shadow-md" role="alert">
                <p className="font-bold">{t('userProfile.updateSuccessTitle', 'Update Successful')}</p>
                <p>{t('userProfile.updateSuccessMessage', 'Your profile has been updated successfully.')}</p>
            </div>
        )}

        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          {/* Content Section */}
          <div className="p-3 sm:p-6 lg:p-8">
            {isEditing ? (
                <form id="profile-edit-form" onSubmit={handleFormSubmit} className="space-y-6">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">{t('userProfile.edit.title', 'Edit Your Information')}</h2>
                    <FormInput
                        label={t('userProfile.edit.fullName', 'Full Name')}
                        name="fullName"
                        value={editFormData.fullName}
                        onChange={handleInputChange}
                        data-testid="profile-form-fullname"
                    />
                    <DateOfBirthField value={editFormData.dateOfBirth} onChange={handleInputChange} />
                    <FormInput
                        label={t('userProfile.email', 'Email (cannot be changed)')} 
                        name="email"
                        value={user?.email || ''} 
                        readOnly 
                    />
                    <FormInput 
                        label={t('userProfile.edit.phone', 'Phone Number')}
                        name="phone"
                        value={editFormData.phone}
                        onChange={handleInputChange}
                        data-testid="profile-form-phone"
                    />
                    <FormInput 
                        label={t('userProfile.edit.address', 'Address')}
                        name="address"
                        value={editFormData.address}
                        onChange={handleInputChange}
                        data-testid="profile-form-address"
                    />
                    <FormInput 
                        label={t('userProfile.edit.city', 'City')}
                        name="city"
                        value={editFormData.city}
                        onChange={handleInputChange}
                        data-testid="profile-form-city"
                    />
                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                        {t('userProfile.edit.country', 'Country')}
                      </label>
                      <select
                        id="country"
                        name="country"
                        value={editFormData.country}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        data-testid="profile-form-country"
                      >
                        <option value="">{t('userProfile.edit.selectCountry', 'Select Country')}</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {getLocalizedCountryName(country.code)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="defaultLanguage" className="block text-sm font-medium text-gray-700">
                        {t('userProfile.edit.defaultLanguage', 'Default Language')}
                      </label>
                      <select
                        id="defaultLanguage"
                        name="defaultLanguage"
                        value={editFormData.defaultLanguage}
                        onChange={handleInputChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        data-testid="profile-form-language"
                      >
                        <option value="MACEDONIAN">{t('languages.macedonian')}</option>
                        <option value="ALBANIAN">{t('languages.albanian')}</option>
                        <option value="ENGLISH">{t('languages.english')}</option>
                        <option value="TURKISH">{t('languages.turkish')}</option>
                        <option value="GERMAN">{t('languages.german')}</option>
                        <option value="SERBIAN">{t('languages.serbian')}</option>
                        <option value="CROATIAN">{t('languages.croatian')}</option>
                      </select>
                    </div>
                    <TermsAcceptance checked={editFormData.ageConfirmed}
                      onChange={e => setEditFormData(prev => ({ ...prev, ageConfirmed: e.target.checked }))} />
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                        <button 
                            type="button" 
                            onClick={handleEditToggle}
                            disabled={isSaving}
                            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                        >
                            <XMarkIcon className="-ml-1 mr-2 h-5 w-5 inline" />
                            {t('userProfile.cancel')}
                        </button>
                        <button 
                            type="submit"
                            disabled={isSaving}
                            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
                            data-testid="profile-form-save"
                        >
                            {isSaving ? (
                                <ArrowPathIcon className="animate-spin h-5 w-5 text-white" />
                            ) : (
                                <>
                                    <CheckIcon className="-ml-1 mr-2 h-5 w-5" />
                                    {t('userProfile.edit.save', 'Save Changes')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            ) : (
                <div className="space-y-8">
                  {/* Memorial Profiles Section - TOP */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
                    {/* Memorial Profiles Header */}
                    <div className="bg-gradient-to-r from-rose-500 to-pink-600 p-4 sm:p-6">
                      <div className="flex flex-row justify-between items-center gap-3">
                        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                          <svg className="h-6 w-6 sm:h-8 sm:w-8 text-white mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2L8 6v2H6a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V10a2 2 0 00-2-2h-2V6l-4-4z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v4M8 14h8" />
                          </svg>
                          <span>{t('userProfile.myProfiles', 'My Memorial Profiles')}</span>
                        </h2>
                        <div className="flex-shrink-0">
                          <CreateProfileButton className="text-sm px-4 py-2 bg-white text-emerald-600 hover:bg-emerald-50 border-0 shadow-md hover:shadow-lg" showIcon={true} />
                        </div>
                      </div>
                      <div className="mt-3 flex items-center">
                        <div className="inline-flex items-center bg-white bg-opacity-20 rounded-full px-4 py-2">
                          <span className="text-rose-100 text-sm font-medium mr-3">{t('userProfile.activeProfiles')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-white text-rose-600 rounded-full text-sm font-bold">
                              {profiles.length}
                            </span>
                            <span className="text-white text-sm">/</span>
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-rose-400 text-white rounded-full text-sm font-bold">
                              {user?.maxProfiles || 1}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Memorial Profiles Content */}
                    <div className="p-4 sm:p-6">
                      {profiles.length === 0 ? (
                        <div className="text-center py-12 px-4">
                          <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="h-10 w-10 text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('userProfile.noProfiles', 'No profiles yet')}</h3>
                          <p className="text-gray-500 mb-8 max-w-md mx-auto text-lg">{t('userProfile.noProfilesHint', 'Get started by creating a new memorial profile.')}</p>
                          <CreateProfileButton className="inline-block" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 profiles-mobile-grid">
                          {profiles.map(profile => {
                            const profileImageUrl = profile.uploadedImages && profile.uploadedImages.length > 0 
                              ? profile.uploadedImages[0].url
                              : `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random&color=fff&size=400`;

                            return (
                              <div key={profile.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-200 group border border-gray-100 profile-card-mobile">
                                {/* Profile Image */}
                                <div className="relative aspect-square bg-gray-200">
                                  <img
                                    src={profileImageUrl}
                                    alt={profile.name}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </div>

                                {/* Profile Info */}
                                <div className="p-4 profile-info-mobile">
                                  <h3 className="font-semibold text-gray-900 text-lg mb-1 truncate profile-name-mobile">
                                    {profile.name}
                                  </h3>
                                  <p className="text-sm text-gray-600 mb-4 profile-years-mobile">
                                    {(() => {
                                      const birthDate = profile.birthDate;
                                      const deathDate = profile.deathDate;

                                      if (birthDate && deathDate) {
                                        return `${birthDate} - ${deathDate}`;
                                      } else if (birthDate) {
                                        return `${birthDate} - `;
                                      } else if (deathDate) {
                                        return ` - ${deathDate}`;
                                      } else {
                                        return '-';
                                      }
                                    })()}
                                  </p>

                                  {/* Action Buttons */}
                                  <div className="flex justify-between items-center profile-actions-mobile">
                                    <Link
                                      to={`/${lang}/profile/${profile.stringId || profile.id}`}
                                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-rose-600 rounded-lg hover:bg-rose-700 transition-colors flex-1 mr-2 justify-center profile-view-button-mobile"
                                    >
                                      {t('userProfile.viewProfile')}
                                    </Link>
                                    <div className="flex items-center space-x-1 profile-edit-buttons-mobile">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditProfile(profile);
                                        }}
                                        className="p-2 text-gray-400 hover:text-indigo-500 rounded-lg hover:bg-indigo-50 transition-colors"
                                        title={t('userProfile.editProfile', 'Edit Profile')}
                                        data-testid={`edit-profile-${profile.id}`}
                                      >
                                        <PencilSquareIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteProfile(profile);
                                        }}
                                        className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                                        title={t('userProfile.deleteProfile', 'Delete Profile')}
                                        data-testid={`delete-profile-${profile.id}`}
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* QR Codes Section - MIDDLE */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-lg">
                    {/* QR Codes Header */}
                    <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-4 sm:p-6">
                      <div className="flex flex-row justify-between items-center gap-3">
                        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                          <svg className="h-6 w-6 sm:h-8 sm:w-8 text-white mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 6a1 1 0 000 2v2a1 1 0 001 1h2a1 1 0 100-2V9a1 1 0 00-1-1H9zM6 13a1 1 0 100 2h2a1 1 0 100-2H6zM17 11a1 1 0 100-2h-2a1 1 0 100 2h2zM17 15a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM13 17a1 1 0 100-2v-2a1 1 0 10-2 0v2a1 1 0 001 1h1z" clipRule="evenodd" />
                          </svg>
                          <span>{t('userProfile.qrCodes.title')}</span>
                        </h2>
                      </div>
                      <div className="mt-3 flex items-center">
                        <div className="inline-flex items-center bg-white bg-opacity-20 rounded-full px-4 py-2">
                          <span className="text-amber-100 text-sm font-medium mr-3">{t('userProfile.qrCodes.availableCount')}</span>
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-white text-amber-600 rounded-full text-sm font-bold">
                              {userQrCodes.length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* QR Codes Content */}
                    <div className="p-4 sm:p-6">
                      {isLoadingQrCodes ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                          <span className="ml-3 text-gray-600">{t('userProfile.qrCodes.loading')}</span>
                        </div>
                      ) : userQrCodes.length === 0 ? (
                        <div className="text-center py-8">
                          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('userProfile.qrCodes.emptyTitle')}</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {t('userProfile.qrCodes.emptyText')}
                          </p>
                          <div className="mt-6">
                            <Link
                              to={`/${lang}/products`}
                              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700"
                            >
                              <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              {t('userProfile.qrCodes.buy')}
                            </Link>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {userQrCodes.map((qrCode) => (
                            <div 
                              key={qrCode.id} 
                              className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center">
                                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <svg className="w-6 h-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM13 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM9 6a1 1 0 000 2v2a1 1 0 001 1h2a1 1 0 100-2V9a1 1 0 00-1-1H9zM6 13a1 1 0 100 2h2a1 1 0 100-2H6zM17 11a1 1 0 100-2h-2a1 1 0 100 2h2zM17 15a1 1 0 01-1 1h-2a1 1 0 110-2h2a1 1 0 011 1zM13 17a1 1 0 100-2v-2a1 1 0 10-2 0v2a1 1 0 001 1h1z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p 
                                      className="text-sm font-medium text-gray-900 font-mono select-text cursor-text" 
                                      title={t('userProfile.qrCodes.selectToCopy')}
                                    >
                                      {qrCode.code}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {qrCode.assigned ? t('userProfile.qrCodes.assigned') : t('userProfile.qrCodes.unassigned')}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center">
                                  {qrCode.assigned ? (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {t('userProfile.qrCodes.active')}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      {t('userProfile.qrCodes.available')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {qrCode.assigned && qrCode.profileName && (
                                <div className="mt-2 p-2 bg-white rounded border border-amber-200">
                                  <p className="text-xs text-gray-600">{t('userProfile.qrCodes.assignedTo')}</p>
                                  <p className="text-sm font-medium text-gray-900">{qrCode.profileName}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* User Account Details Section - BOTTOM */}
                  <div className="bg-gradient-to-br from-slate-50 via-white to-slate-50 rounded-2xl border border-slate-200 overflow-hidden shadow-xl">
                    {/* Account Details Header */}
                    <div className="relative bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 p-6 sm:p-8">
                      {/* Decorative background pattern */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400 rounded-full blur-3xl transform -translate-x-24 translate-y-24"></div>
                      </div>
                      
                      <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-start space-x-4">
                          <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30 ring-4 ring-white/10">
                            <UserCircleIcon className="h-8 w-8 text-white" />
                          </div>
                          <div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-1 tracking-tight" data-testid="user-profile-fullname-heading">
                              {user?.fullName}
                            </h2>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                              <p className="text-slate-300 text-sm font-medium">
                                {t('userProfile.accountDetails')}
                              </p>
                            </div>
                          </div>
                        </div>
                        <button 
                            onClick={handleEditToggle} 
                            className="group inline-flex items-center px-5 py-2.5 border-2 border-white/20 text-sm font-semibold rounded-xl text-white bg-white/10 hover:bg-white/20 hover:border-white/30 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-slate-800 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
                            data-testid="profile-edit-button"
                        >
                            <PencilSquareIcon className="-ml-1 mr-2.5 h-5 w-5 group-hover:rotate-12 transition-transform duration-200" />
                            {t('userProfile.editAccount')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Account Details Content */}
                    <div className="p-6 sm:p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Full Name Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <UserCircleIcon className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.fullName')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate">
                                {user?.fullName || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Email Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-emerald-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <EnvelopeIcon className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.email')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate">
                                {user?.email || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Phone Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-purple-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <PhoneIcon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.phone')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate" data-testid="user-profile-phone-display">
                                {user?.phone || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Address Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-50 to-orange-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-amber-100 to-amber-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <MapPinIcon className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.address')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate" data-testid="user-profile-address-display">
                                {user?.address || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* City Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-cyan-50 to-sky-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <svg className="h-6 w-6 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.city')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate" data-testid="user-profile-city-display">
                                {user?.city || '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Country Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-rose-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-rose-50 to-pink-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-rose-100 to-rose-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <GlobeAltIcon className="h-6 w-6 text-rose-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.country')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate" data-testid="user-profile-country-display">
                                {getLocalizedCountryName(user?.country)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Language Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-indigo-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('userProfile.defaultLanguage')}
                              </p>
                              <p className="text-base font-semibold text-slate-900 truncate" data-testid="user-profile-language-display">
                                {user?.defaultLanguage ? t(`languages.${user.defaultLanguage.toLowerCase()}`, user.defaultLanguage) : '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Password Card */}
                        <div className="group relative bg-white rounded-xl p-5 border border-slate-200 hover:border-red-300 hover:shadow-lg transition-all duration-300 overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-50 to-rose-50 rounded-bl-full opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative flex items-start space-x-4">
                            <div className="flex-shrink-0 w-11 h-11 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                              <LockClosedIcon className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                {t('changePassword.passwordLabel')}
                              </p>
                              <div className="flex items-center justify-between">
                                <p className="text-base font-semibold text-slate-900">
                                  ••••••••••••
                                </p>
                                <button
                                  onClick={() => setShowChangePasswordModal(true)}
                                  className="ml-4 px-4 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 rounded-lg transition-all duration-200 hover:shadow-md"
                                >
                                  {t('changePassword.changeButton')}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Delete Modal */}
      <ProfileDeleteModal
        profile={deletingProfile}
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingProfile(null);
        }}
        onDelete={handleProfileDeleted}
      />

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={() => {
                setShowChangePasswordModal(false);
                setPasswordError(null);
                setPasswordSuccess(false);
                setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}
            ></div>

            {/* Modal panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handlePasswordChange}>
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 to-rose-700 px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                      <LockClosedIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white" id="modal-title">
                        {t('changePassword.title')}
                      </h3>
                      <p className="text-sm text-red-100 mt-0.5">
                        {t('changePassword.subtitle')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="bg-white px-6 py-5 space-y-4">
                  {passwordError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                      <p className="text-sm text-red-700">{passwordError}</p>
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                      <p className="text-sm text-green-700">
                        {t('changePassword.success')}
                      </p>
                    </div>
                  )}

                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('changePassword.currentPasswordLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        id="currentPassword"
                        value={passwordFormData.currentPassword}
                        onChange={(e) => setPasswordFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        placeholder={t('changePassword.currentPasswordPlaceholder')}
                        className="block w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        aria-label={t(showCurrentPassword ? 'changePassword.hidePassword' : 'changePassword.showPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('changePassword.newPasswordLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="newPassword"
                        value={passwordFormData.newPassword}
                        onChange={(e) => setPasswordFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder={t('changePassword.newPasswordPlaceholder')}
                        className="block w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        aria-label={t(showNewPassword ? 'changePassword.hidePassword' : 'changePassword.showPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('changePassword.passwordRequirement')}
                    </p>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('changePassword.confirmPasswordLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={passwordFormData.confirmPassword}
                        onChange={(e) => setPasswordFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder={t('changePassword.confirmPasswordPlaceholder')}
                        className="block w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        disabled={isChangingPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={t(showConfirmPassword ? 'changePassword.hidePassword' : 'changePassword.showPassword')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePasswordModal(false);
                      setPasswordError(null);
                      setPasswordSuccess(false);
                      setPasswordFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    }}
                    disabled={isChangingPassword}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {t('changePassword.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="inline-flex justify-center items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all"
                  >
                    {isChangingPassword ? (
                      <>
                        <ArrowPathIcon className="animate-spin -ml-1 mr-2 h-5 w-5" />
                        {t('changePassword.changing')}
                      </>
                    ) : (
                      <>
                        <LockClosedIcon className="-ml-1 mr-2 h-5 w-5" />
                        {t('changePassword.changeButton')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile; 
